/**
 * The base stylesheet every Custom View page gets, between the theme's variables and the page's own styles:
 * a reset and a small kit of classes in the Morta look, built only from the --morta-* tokens so it follows
 * the hub and the reader's light or dark theme. Pages compose these rather than restyling each element,
 * which keeps every page on the same scale. A page's own styles come after, so it can still adjust them.
 *
 * - Layout: #morta-root (the page's gutter), .m-narrow (a document's width, centred), .m-title, .m-subtitle,
 *   .m-heading, .m-grid
 * - Surfaces: .m-card with .m-card-header, .m-card-title and .m-card-body; .m-kpi (.m-kpi-label, -value, -note)
 * - Data: .m-table (in a .m-card, sticky header, .num columns), .m-badge with data-tone
 * - Controls: .m-btn (.m-btn-secondary, .m-btn-ghost, .m-btn-danger), .m-field with .m-input and .m-hint
 * - States: .m-skeleton, .m-state with data-tone, .m-tooltip
 * - Forms, in the form view's look: .m-form (two columns; data-width="2" spans both), .m-label (a field's name,
 *   then its .m-required *), .m-field-description, label.m-check (a 20px checkbox and its word), .m-dropzone
 *   (with .m-dropzone-icon and a .m-link to choose files), ul.m-files of li.m-file (.m-file-name, .m-file-size,
 *   an .m-icon-btn), .m-card-footer (the grey band with the card's buttons at its end) and .m-sr-only
 * - Charts: svg.m-chart (with .m-chart-grid, .m-chart-cursor, .m-chart-value, .m-chart-line, .m-chart-area,
 *   .m-chart-dot, .m-chart-crosshair, .m-chart-slice, .m-chart-total, .m-chart-reference),
 *   .m-chart-legend and .m-chart-tooltip, which morta.chart's helpers draw
 * - Data: bound elements (data-morta-column / -row / -agg) and data-morta-show="loading|empty|error|ok" blocks
 */
export const KIT_CSS = `
*, *::before, *::after { box-sizing: border-box; }
html { -webkit-text-size-adjust: 100%; }
body {
  margin: 0; background: var(--morta-surface-sunken); color: var(--morta-text);
  font: 400 var(--morta-text-body)/var(--morta-leading-body) var(--morta-font); -webkit-font-smoothing: antialiased;
}
h1, h2, h3, h4, h5, h6, p, figure { margin: 0; }
button, input, select, textarea { font: inherit; color: inherit; }
[hidden] { display: none !important; }

#morta-root { display: flex; flex-direction: column; gap: var(--morta-gap-lg); padding: var(--morta-pad-xl); }
@media (max-width: 600px) { #morta-root { padding: var(--morta-pad-lg); } }
/* A page that would sprawl on a wide screen (a form, one record) wraps its content in this. */
.m-narrow {
  display: flex; flex-direction: column; gap: var(--morta-gap-lg);
  width: 100%; max-width: 880px; margin-inline: auto;
}

.m-title { font-size: var(--morta-text-title); line-height: var(--morta-leading-title); font-weight: 600; }
.m-subtitle { margin-top: 2px; color: var(--morta-muted); }
.m-heading { font-size: var(--morta-text-heading); line-height: var(--morta-leading-heading); font-weight: 600; }
.m-grid {
  display: grid; gap: var(--morta-gap-lg);
  grid-template-columns: repeat(auto-fit, minmax(min(100%, var(--m-min, 220px)), 1fr));
}

.m-card {
  background: var(--morta-surface); border: 1px solid var(--morta-border); border-radius: var(--morta-radius);
  overflow: hidden;
}
.m-card-header {
  display: flex; align-items: center; justify-content: space-between; gap: var(--morta-gap-md);
  padding: var(--morta-pad-md) var(--morta-pad-lg); border-bottom: 1px solid var(--morta-border);
}
.m-card-title { font-size: var(--morta-text-body); line-height: var(--morta-leading-body); font-weight: 600; }
.m-card-body { padding: var(--morta-pad-lg); }

.m-kpi { display: flex; flex-direction: column; gap: var(--morta-gap-xs); padding: var(--morta-pad-lg); }
.m-kpi-label { font-size: var(--morta-text-caption); line-height: var(--morta-leading-caption); color: var(--morta-muted); }
.m-kpi-value { font-size: 24px; line-height: 32px; font-weight: 600; font-variant-numeric: tabular-nums; }
.m-kpi-note { font-size: var(--morta-text-caption); line-height: var(--morta-leading-caption); color: var(--morta-muted); }

.m-table-scroll { overflow: auto; }
.m-table { width: 100%; border-collapse: separate; border-spacing: 0; }
.m-table th {
  position: sticky; top: 0; z-index: 1; padding: var(--morta-pad-sm) var(--morta-pad-md); text-align: left;
  background: var(--morta-surface); border-bottom: 1px solid var(--morta-border); white-space: nowrap;
  font-size: var(--morta-text-caption); line-height: var(--morta-leading-caption); font-weight: 500;
  color: var(--morta-muted);
}
.m-table td {
  padding: var(--morta-pad-sm) var(--morta-pad-md); border-bottom: 1px solid var(--morta-border);
  white-space: nowrap; max-width: 320px; overflow: hidden; text-overflow: ellipsis;
}
.m-table tbody tr:last-child td { border-bottom: 0; }
.m-table tbody tr:hover td { background: color-mix(in srgb, var(--morta-text) 4%, var(--morta-surface)); }
.m-table .num { text-align: right; font-variant-numeric: tabular-nums; }

.m-badge {
  display: inline-flex; align-items: center; gap: var(--morta-gap-xs); height: 20px; padding: 0 8px;
  border-radius: 999px; border: 1px solid var(--morta-border); white-space: nowrap;
  background: color-mix(in srgb, var(--morta-text) 4%, var(--morta-surface)); color: var(--morta-text);
  font-size: var(--morta-text-caption); font-weight: 500;
}
.m-badge[data-tone="good"] { border-color: transparent; background: color-mix(in srgb, var(--morta-good) 14%, var(--morta-surface)); color: var(--morta-text); }
.m-badge[data-tone="caution"] { border-color: transparent; background: color-mix(in srgb, var(--morta-caution) 20%, var(--morta-surface)); color: var(--morta-text); }
.m-badge[data-tone="critical"] { border-color: transparent; background: color-mix(in srgb, var(--morta-critical) 14%, var(--morta-surface)); color: var(--morta-critical); }
.m-badge[data-tone="info"] { border-color: transparent; background: color-mix(in srgb, var(--morta-info) 12%, var(--morta-surface)); color: var(--morta-info); }

.m-btn {
  display: inline-flex; align-items: center; justify-content: center; gap: 6px; height: 32px; padding: 0 12px;
  border-radius: var(--morta-radius-sm); border: 1px solid transparent; cursor: pointer; white-space: nowrap;
  font-size: var(--morta-text-body); font-weight: 500; line-height: 1;
  background: var(--morta-button-bg); color: var(--morta-button-text);
  transition: opacity var(--morta-duration-fast), background-color var(--morta-duration-fast);
}
.m-btn:hover:not(:disabled) { opacity: 0.9; }
.m-btn:focus-visible { outline: 2px solid var(--morta-info); outline-offset: 2px; }
.m-btn:disabled { opacity: 0.45; cursor: not-allowed; }
.m-btn-secondary { background: var(--morta-surface); color: var(--morta-text); border-color: var(--morta-border); }
.m-btn-ghost { background: transparent; color: var(--morta-text); }
.m-btn-secondary:hover:not(:disabled), .m-btn-ghost:hover:not(:disabled) {
  opacity: 1; background: color-mix(in srgb, var(--morta-text) 4%, var(--morta-surface));
}
.m-btn-danger { background: var(--morta-danger); color: var(--morta-on-solid); }

.m-field { display: flex; flex-direction: column; gap: 6px; }
.m-field > label { font-weight: 500; }
.m-field .m-required { color: var(--morta-danger); margin-left: 2px; }
.m-hint { font-size: var(--morta-text-caption); line-height: var(--morta-leading-caption); color: var(--morta-muted); }
.m-input, .m-field input:not([type="checkbox"]):not([type="radio"]), .m-field select, .m-field textarea {
  width: 100%; height: 32px; padding: 0 10px; border: 1px solid var(--morta-border);
  border-radius: var(--morta-radius-sm); background: var(--morta-field); outline: none;
  transition: border-color var(--morta-duration-fast), box-shadow var(--morta-duration-fast);
}
.m-field textarea, textarea.m-input { height: auto; min-height: 80px; padding: 8px 10px; resize: vertical; }
/* A select in the kit's own look rather than the browser's: its text lined up with the other fields, and a
   small chevron drawn in the muted text colour (two gradients, so no colour is typed). */
select.m-input, .m-field select {
  appearance: none; -webkit-appearance: none; padding-right: 32px; cursor: pointer;
  background-image:
    linear-gradient(45deg, transparent 50%, var(--morta-muted) 50%),
    linear-gradient(135deg, var(--morta-muted) 50%, transparent 50%);
  background-position: calc(100% - 16px) 50%, calc(100% - 12px) 50%;
  background-size: 4px 4px, 4px 4px;
  background-repeat: no-repeat;
}
.m-input:focus, .m-field input:focus, .m-field select:focus, .m-field textarea:focus {
  border-color: var(--morta-info); box-shadow: 0 0 0 3px color-mix(in srgb, var(--morta-info) 15%, transparent);
}
.m-field[data-invalid] input, .m-field[data-invalid] select, .m-field[data-invalid] textarea { border-color: var(--morta-danger); }
.m-field input:disabled, .m-field select:disabled, .m-field textarea:disabled {
  background: var(--morta-surface-sunken); cursor: not-allowed;
}

.m-skeleton {
  display: block; height: 12px; border-radius: 6px; background: var(--morta-skeleton);
  background-image: linear-gradient(90deg, transparent, color-mix(in srgb, var(--morta-surface) 50%, transparent), transparent);
  background-size: 200% 100%; background-repeat: no-repeat; animation: m-shimmer 1.4s linear infinite;
}
@keyframes m-shimmer { from { background-position: 150% 0; } to { background-position: -50% 0; } }

.m-state { padding: 48px 24px; text-align: center; color: var(--morta-muted); }
.m-state strong { display: block; margin-bottom: 4px; color: var(--morta-text); font-weight: 600; }
.m-state[data-tone="error"] { color: var(--morta-danger); }

.m-tooltip {
  position: fixed; z-index: 10; pointer-events: none; max-width: 240px; padding: 6px 8px;
  background: var(--morta-popover-bg); border: 1px solid var(--morta-popover-border);
  border-radius: var(--morta-radius-sm); box-shadow: var(--morta-popover-shadow);
  font-size: var(--morta-text-caption); line-height: var(--morta-leading-caption);
}

/* Forms, in the form view's look: a name over each field (a red * after a required one), its description in
   the muted text, and fields a little taller and rounder than a toolbar's, filling the card's two columns. */
.m-form {
  display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: var(--morta-gap-xl) var(--morta-gap-md);
}
.m-form > [data-width="2"] { grid-column: 1 / -1; }
@media (max-width: 640px) { .m-form { grid-template-columns: minmax(0, 1fr); } }
.m-form .m-field { min-width: 0; }
.m-label { display: flex; align-items: center; gap: 6px; min-height: 24px; min-width: 0; font-weight: 500; }
.m-label > label, .m-label > span:first-child { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.m-label .m-required { margin-left: 0; }
.m-field-description { margin-top: -2px; color: var(--morta-muted); }
.m-field-description ul, .m-field-description ol { margin: 4px 0 0; padding-left: 20px; }
.m-form .m-field input:not([type="checkbox"]):not([type="radio"]):not([type="file"]), .m-form .m-field select {
  height: 34px; padding-inline: 12px; border-radius: var(--morta-radius);
}
.m-form .m-field textarea { padding: 7px 12px; border-radius: var(--morta-radius); }
.m-form .m-field select { padding-right: 32px; }
.m-form .m-field input:hover:not(:focus):not(:disabled), .m-form .m-field select:hover:not(:focus):not(:disabled),
.m-form .m-field textarea:hover:not(:focus):not(:disabled) {
  border-color: color-mix(in srgb, var(--morta-text) 28%, var(--morta-border));
}
/* A checkbox with the word it stands for after it, the two one target. */
.m-check { display: inline-flex; align-items: center; gap: var(--morta-gap-sm); min-height: 34px; cursor: pointer; }
.m-check input[type="checkbox"] {
  width: 20px; height: 20px; margin: 0; flex: none; cursor: pointer; accent-color: var(--morta-info);
}
/* Where files are dropped or chosen: a dashed box with its icon on a tile, and a link to choose them. */
.m-dropzone {
  display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 10px;
  min-height: 112px; padding: var(--morta-pad-xl) var(--morta-pad-lg); text-align: center; cursor: pointer;
  border: 1px dashed var(--morta-border); border-radius: var(--morta-radius); background: var(--morta-field);
  color: var(--morta-muted); transition: border-color var(--morta-duration-fast), background-color var(--morta-duration-fast);
}
.m-dropzone:hover { border-color: color-mix(in srgb, var(--morta-text) 28%, var(--morta-border)); }
.m-dropzone[data-dragging] {
  border-color: var(--morta-info); background: color-mix(in srgb, var(--morta-info) 5%, var(--morta-field));
}
.m-dropzone-icon {
  display: flex; align-items: center; justify-content: center; width: 32px; height: 32px;
  border: 1px solid var(--morta-border); border-radius: var(--morta-radius-sm); background: var(--morta-surface);
  color: var(--morta-text); box-shadow: 0 1px 2px color-mix(in srgb, var(--morta-text) 6%, transparent);
}
.m-dropzone-icon svg, .m-file svg, .m-icon-btn svg { width: 16px; height: 16px; }
/* A link-like button in the text's own colour, underlined on hover. */
.m-link {
  padding: 0; border: 0; background: none; cursor: pointer; font-weight: 500; color: var(--morta-text);
  text-decoration: underline transparent; text-underline-offset: 2px;
}
.m-link:hover { text-decoration-color: currentColor; }
.m-link:focus-visible { outline: 2px solid var(--morta-info); outline-offset: 2px; border-radius: 2px; }
/* Files, one to a row: an icon, the name (cut short with …), the size, and a button or two at the end. */
.m-files {
  margin: 0; padding: 0; list-style: none; border: 1px solid var(--morta-border); border-radius: var(--morta-radius);
  background: var(--morta-field);
}
.m-files:empty { display: none; }
.m-file { display: flex; align-items: center; gap: 10px; min-height: 44px; padding: 0 6px 0 12px; min-width: 0; }
.m-file + .m-file { border-top: 1px solid var(--morta-border); }
.m-file > svg { flex: none; color: var(--morta-muted); }
.m-file-name { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.m-file-size { flex: none; color: var(--morta-muted); font-variant-numeric: tabular-nums; }
.m-file[data-pending] { color: var(--morta-muted); }
.m-icon-btn {
  display: inline-flex; align-items: center; justify-content: center; flex: none; width: 28px; height: 28px;
  padding: 0; border: 0; border-radius: var(--morta-radius-sm); background: transparent; color: var(--morta-muted);
  cursor: pointer;
}
.m-icon-btn:hover { background: color-mix(in srgb, var(--morta-text) 5%, transparent); color: var(--morta-text); }
.m-icon-btn:focus-visible { outline: 2px solid var(--morta-info); outline-offset: 0; }
/* A card's foot: the menus' quieter band, to the card's corners, with its buttons at the end. */
.m-card-footer {
  display: flex; flex-wrap: wrap; align-items: center; justify-content: flex-end; gap: var(--morta-gap-sm) var(--morta-gap-md);
  padding: 14px var(--morta-pad-xl); border-top: 1px solid var(--morta-border);
  background: color-mix(in srgb, var(--morta-text) 2%, var(--morta-surface));
}
.m-sr-only {
  position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0, 0, 0, 0);
  white-space: nowrap; border: 0;
}

/* Charts, in the look of shadcn's: d3 draws plain SVG into an svg.m-chart and these style it, so every chart
   follows a change here. Axes show their labels only (no domain or tick lines); the grid is light lines across
   the value axis; the hovered category gets a soft band behind it. */
.m-chart {
  display: block; width: 100%; overflow: visible; font-size: var(--morta-text-caption);
  font-variant-numeric: tabular-nums;
}
/* d3's axes set font-size="10" and font-family="sans-serif" on their groups; the page's type wins instead. */
.m-chart g { font-family: inherit; font-size: inherit; }
.m-chart text { fill: var(--morta-muted); }
.m-chart .domain, .m-chart .tick line { display: none; }
.m-chart .m-chart-grid .tick line { display: inline; stroke: var(--morta-chart-grid); }
.m-chart .m-chart-grid text { display: none; }
.m-chart .m-chart-cursor { fill: color-mix(in srgb, var(--morta-text) 5%, transparent); }
.m-chart .m-chart-value { fill: var(--morta-text); font-weight: 500; }
.m-chart .m-chart-line { fill: none; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }
.m-chart .m-chart-reference { stroke: var(--morta-chart-reference); stroke-width: 1; stroke-dasharray: 4 4; }
/* An area is a wash of its series under its line; a point and a pie slice keep a 2px ring of the surface, so
   they stay apart where they meet; the crosshair is a hairline. */
.m-chart .m-chart-area { fill-opacity: 0.2; stroke: none; }
.m-chart .m-chart-dot { stroke: var(--morta-surface); stroke-width: 2; }
.m-chart .m-chart-crosshair { stroke: var(--morta-chart-axis); stroke-width: 1; }
.m-chart .m-chart-slice { stroke: var(--morta-surface); stroke-width: 2; transition: opacity var(--morta-duration-fast); }
.m-chart:has(.m-chart-slice:hover) .m-chart-slice:not(:hover) { opacity: 0.6; }
.m-chart .m-chart-total { fill: var(--morta-text); font-size: var(--morta-text-title); font-weight: 600; }
.m-chart-legend {
  display: flex; flex-wrap: wrap; justify-content: center; gap: var(--morta-gap-xs) var(--morta-gap-lg);
  padding-top: var(--morta-pad-sm); font-size: var(--morta-text-caption); line-height: var(--morta-leading-caption);
  color: var(--morta-muted);
}
.m-chart-legend-item { display: inline-flex; align-items: center; gap: 6px; }
.m-chart-swatch { flex: none; width: 10px; height: 10px; border-radius: 2px; background: var(--m-swatch); }
/* A line's key is a short stroke rather than a square. */
.m-chart-swatch[data-key="line"] { width: 12px; height: 2px; border-radius: 1px; }
.m-chart-tooltip {
  position: fixed; z-index: 10; pointer-events: none; display: grid; gap: 6px; min-width: 128px; padding: 6px 10px;
  background: var(--morta-popover-bg); border: 1px solid var(--morta-popover-border);
  border-radius: var(--morta-radius); box-shadow: var(--morta-popover-shadow);
  font-size: var(--morta-text-caption); line-height: var(--morta-leading-caption);
}
.m-chart-tooltip-title { font-weight: 500; }
.m-chart-tooltip-row { display: flex; align-items: center; gap: var(--morta-gap-sm); }
.m-chart-tooltip-name { color: var(--morta-muted); }
.m-chart-tooltip-value { margin-left: auto; padding-left: var(--morta-pad-md); font-weight: 500; font-variant-numeric: tabular-nums; }

/* Bound elements and the page's data status (html[data-morta-status]: loading, ok, empty, error), which the
   host sets as it reads the view: bound numbers shimmer while loading; [data-morta-show] blocks appear only
   for their status; a bound number can be clicked to see where it came from. */
/* Hidden unless the status matches, so a block that does show keeps its own display (a .m-grid stays a grid). */
html:not([data-morta-status]) [data-morta-show],
html[data-morta-status="loading"] [data-morta-show]:not([data-morta-show~="loading"]),
html[data-morta-status="empty"] [data-morta-show]:not([data-morta-show~="empty"]),
html[data-morta-status="error"] [data-morta-show]:not([data-morta-show~="error"]),
html[data-morta-status="ok"] [data-morta-show]:not([data-morta-show~="ok"]) { display: none !important; }
/* A page can still hide a status block itself (e.g. an ok block with nothing to show). */
html[data-morta-status] [data-morta-show][hidden] { display: none !important; }
html[data-morta-status="loading"] :is([data-morta-column], [data-morta-agg]) {
  display: inline-block; min-width: 3em; color: transparent; border-radius: 6px; background: var(--morta-skeleton);
  animation: m-pulse 1.4s ease-in-out infinite;
}
@keyframes m-pulse { 50% { opacity: 0.55; } }
[data-morta-bound] { cursor: pointer; text-decoration: underline dotted transparent; text-underline-offset: 3px; }
[data-morta-bound]:hover { text-decoration-color: var(--morta-muted); }
[data-morta-missing] { color: var(--morta-muted); cursor: help; }

@media (prefers-reduced-motion: reduce) {
  .m-skeleton, html[data-morta-status="loading"] :is([data-morta-column], [data-morta-agg]) { animation: none; }
  .m-btn, .m-input, .m-field input, .m-field select, .m-field textarea, .m-dropzone { transition: none; }
}
`
